// Asks the browser to make this origin's storage *persistent*.
//
// By default IndexedDB is "best-effort" storage: under storage pressure the
// browser may evict the entire `knittime` store — every pattern and session —
// with no warning. For a local-first app whose primary copy of the user's data
// lives in IndexedDB, that is the single biggest silent-data-loss vector,
// especially for users who haven't opted into Gist sync.
//
// `navigator.storage.persist()` requests the "persistent" bucket, which is
// exempt from automatic eviction. On Chromium it is granted silently via
// engagement heuristics; on Firefox it may show a one-time prompt; on Safari it
// is granted for installed PWAs. We request it once at boot and never re-prompt
// once already granted.

export type PersistResult = 'persisted' | 'denied' | 'unsupported' | 'error';

/**
 * Request persistent storage if it isn't already granted.
 *
 * Returns:
 * - `'persisted'`  — storage is (now or already) persistent
 * - `'denied'`     — the browser declined the request
 * - `'unsupported'`— the Storage Manager / persist API is unavailable
 * - `'error'`      — the request threw
 *
 * Never throws; safe to call unconditionally at startup.
 */
export async function requestPersistentStorage(): Promise<PersistResult> {
  const storage =
    typeof navigator !== 'undefined' ? navigator.storage : undefined;
  if (!storage || typeof storage.persist !== 'function') {
    return 'unsupported';
  }

  try {
    // Avoid re-requesting (and re-prompting on Firefox) when already granted.
    if (typeof storage.persisted === 'function' && (await storage.persisted())) {
      return 'persisted';
    }
    const granted = await storage.persist();
    return granted ? 'persisted' : 'denied';
  } catch {
    return 'error';
  }
}
